import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { paymentsAPI } from "../services/api";
import { PageWrapper, Card, Button } from "../components";
import { ArrowLeft, Crown, Check, Sparkles, CreditCard, HelpCircle } from "lucide-react";

export const UpgradePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const plans = {
    monthly: {
      name: "Monthly Membership",
      price: 29,
      description: "Full access to all programs and features",
      interval: "month"
    },
    annual: {
      name: "Annual Membership",
      price: 249,
      description: "Full access to all programs - Save 28%!",
      interval: "year",
      badge: "Best Value"
    }
  };

  const handleUpgrade = async (planId) => {
    setLoading(planId);
    try {
      const originUrl = window.location.origin;
      const res = await paymentsAPI.createCheckout(planId, originUrl);
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      console.error(e);
      alert("Failed to initiate checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  if (user?.membership_tier === "paid") {
    return (
      <PageWrapper>
        <div className="p-6 max-w-lg mx-auto space-y-6">
          <button
            onClick={() => navigate("/account")}
            className="flex items-center gap-2 text-[#78716C] hover:text-[#4A6741]"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <Card className="p-8 text-center space-y-4">
            <div className="w-20 h-20 bg-gradient-to-r from-[#D4A373] to-[#B8956A] rounded-full flex items-center justify-center mx-auto">
              <Crown className="text-white" size={40} />
            </div>
            <h2 className="font-heading text-2xl text-[#292524]">You're a Premium Member!</h2>
            <p className="text-[#78716C]">
              You have full access to all programs, lessons, and features.
            </p>
            <Button variant="outline" onClick={() => navigate("/programs")}>
              Explore Programs
            </Button>
          </Card>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="p-6 max-w-lg mx-auto space-y-6">
        <button
          onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-[#78716C] hover:text-[#4A6741]"
        >
          <ArrowLeft size={20} /> Back
        </button>

        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4A373]/10 rounded-full text-[#D4A373] mb-4">
            <Sparkles size={18} />
            <span className="font-medium">Upgrade to Premium</span>
          </div>
          <h1 className="font-heading text-3xl text-[#292524]">Unlock Your Full Potential</h1>
          <p className="text-[#78716C]">Get unlimited access to all programs and features</p>
        </div>

        {/* Features */}
        <Card className="p-5 space-y-3">
          <h3 className="font-heading text-lg text-[#292524]">Premium includes:</h3>
          <ul className="space-y-2">
            {[
              "Access to all current and future programs",
              "Unlimited lessons and practices",
              "Save your reflections forever",
              "Track your progress and streaks",
              "Priority support"
            ].map((feature, idx) => (
              <li key={idx} className="flex items-center gap-3 text-[#78716C]">
                <Check className="text-[#4A6741] flex-shrink-0" size={18} />
                {feature}
              </li>
            ))}
          </ul>
        </Card>

        {/* Plans */}
        <div className="space-y-4">
          {Object.entries(plans).map(([id, plan]) => (
            <Card 
              key={id} 
              className={`p-5 relative ${id === "annual" ? "border-2 border-[#D4A373]" : ""}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 bg-[#D4A373] text-white text-xs font-medium rounded-full">
                    {plan.badge}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg text-[#292524]">{plan.name}</h3>
                  <p className="text-sm text-[#78716C]">{plan.description}</p>
                </div>
                <div className="text-right">
                  <p className="font-heading text-2xl text-[#292524]">${plan.price}</p>
                  <p className="text-xs text-[#78716C]">per {plan.interval}</p>
                </div>
              </div>
              <Button
                variant={id === "annual" ? "premium" : "outline"}
                onClick={() => handleUpgrade(id)}
                disabled={loading === id}
                className="w-full"
                data-testid={`upgrade-${id}-btn`}
              >
                {loading === id ? "Processing..." : `Choose ${plan.name.split(" ")[0]}`}
              </Button>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-[#78716C]">
          Secure checkout powered by Stripe
        </p>
      </div>
    </PageWrapper>
  );
};

export const PaymentSuccessPage = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Verifying your payment...");

  useEffect(() => {
    const sessionId = new URLSearchParams(location.search).get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus("error");
      setMessage("No payment session found.");
    }
  }, [location.search]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      setMessage("Payment verification timed out. Please check your email for confirmation.");
      return;
    }

    try {
      const res = await paymentsAPI.getStatus(sessionId);
      
      if (res.data.payment_status === "paid") {
        setStatus("success");
        setMessage("Payment successful! Your membership has been upgraded.");
        await refreshUser();
        return;
      } else if (res.data.status === "expired") {
        setStatus("error");
        setMessage("Payment session expired. Please try again.");
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (e) {
      console.error(e);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus("error");
        setMessage("Error verifying payment. Please contact support.");
      }
    }
  };

  return (
    <PageWrapper showNav={false}>
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="max-w-sm w-full p-8 text-center space-y-6 animate-fade-in-up">
          {status === "checking" && (
            <>
              <div className="w-20 h-20 bg-[#E6E2DD] rounded-full flex items-center justify-center mx-auto animate-pulse-gentle">
                <CreditCard className="text-[#78716C]" size={40} />
              </div>
              <h2 className="font-heading text-2xl text-[#292524]">Verifying Payment</h2>
              <p className="text-[#78716C]">{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 bg-gradient-to-r from-[#D4A373] to-[#B8956A] rounded-full flex items-center justify-center mx-auto">
                <Crown className="text-white" size={40} />
              </div>
              <h2 className="font-heading text-2xl text-[#292524]">Welcome to Premium!</h2>
              <p className="text-[#78716C]">{message}</p>
              <Button onClick={() => navigate("/")} className="w-full" data-testid="go-home-btn">
                Start Exploring
              </Button>
            </>
          )}

          {(status === "error" || status === "timeout") && (
            <>
              <div className="w-20 h-20 bg-[#E6E2DD] rounded-full flex items-center justify-center mx-auto">
                <HelpCircle className="text-[#78716C]" size={40} />
              </div>
              <h2 className="font-heading text-2xl text-[#292524]">
                {status === "timeout" ? "Verification Timeout" : "Something Went Wrong"}
              </h2>
              <p className="text-[#78716C]">{message}</p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => navigate("/account")} className="flex-1">
                  Go Back
                </Button>
                <Button onClick={() => navigate("/upgrade")} className="flex-1">
                  Try Again
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </PageWrapper>
  );
};
